import {
  getGridStringOperators,
  GridColDef,
  GridFilterModel,
  GridLogicOperator,
  GridInitialState,
} from '@mui/x-data-grid-pro';
import { useCallback, useMemo, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { getDecomLogs, UserKeys } from '../../../../api/users-api';
import AvatarChip from '../../../../shared/avatar-chip';
import { DecomLogModel, Page } from '../../../../../types';
import DecomLogGridToolbar from './decom-log-grid-toolbar';
import { decomActions } from '../../../../shared/decom-actions';
import { useSearchParams } from 'react-router-dom';
import { UserFilter } from './decom-user-filter';
import DecomAcknowledgeDialog from './decom-log-acknowledge-dialog';
import DecomCompleteDialog from './decom-log-complete-dialog';
import dayjs from 'dayjs';
import { createGridOverlays } from '../../../grid-overlay/no-data-overlay-helpers';
import useGridColumns from '../../../../shared/hooks/use-grid-columns-latest';
import { useUserSettings } from '../../../../utils/use-user-settings';
import SharedDataGridServer from '../../../../shared/shared-data-grid-server';
import { useToolbar } from '../../../../shared/shared-data-grid-toolbar-helper';
import History from '@mui/icons-material/History';

export default function DecomLogsGrid() {
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const { viewType } = useUserSettings();
  const userId = searchParams.get('userId');
  const [acknowledgedFilter, setAcknowledgedFilter] = useState<boolean | null>(
    false
  );

  const columns: GridColDef<DecomLogModel>[] = useMemo(
    () => [
      {
        field: 'date',
        headerName: 'Action Time',
        width: 175,
        type: 'dateTime',
        filterable: false,
        sortable: false,
        pinnable: false,
        display: 'flex',
        valueGetter: (value: number) => {
          const date = new Date(value);
          if (value === 0) {
            return null;
          } else {
            return date;
          }
        },
        valueFormatter: (params: Date | null) => {
          if (params === null) {
            return 'N/A';
          } else {
            const formattedDate = dayjs(params).format('MMM D, YYYY, h:mm A');
            return formattedDate;
          }
        },
      },
      {
        field: 'userEmail',
        headerName: 'Decom User Email',
        display: 'flex',
        flex: 1,
        filterable: true,
        sortable: false,
        pinnable: false,
        renderCell: (params) => {
          return <AvatarChip email={params.value} type="User" />;
        },
        filterOperators: getGridStringOperators()
          .filter((op) => op.value === 'equals')
          .map((operator) => {
            return {
              ...operator,
              InputComponent: operator.InputComponent ? UserFilter : undefined,
            };
          }),
      },
      {
        field: 'decomName',
        headerName: 'Policy Name',
        display: 'flex',
        flex: 1,
        minWidth: 170,
        filterable: false,
        sortable: false,
        pinnable: false,
      },
      {
        field: 'message',
        headerName: 'Message',
        display: 'flex',
        flex: 1,
        filterable: false,
        sortable: false,
        pinnable: false,
      },
      {
        field: 'errorMessage',
        headerName: 'Error Message',
        display: 'flex',
        flex: 1,
        filterable: false,
        sortable: false,
        pinnable: false,
        valueFormatter: (params: string | null) => {
          if (params === null) {
            return 'N/A';
          } else {
            return params;
          }
        },
      },
      {
        field: 'actionType',
        headerName: 'Action',
        display: 'flex',
        flex: 1,
        filterable: false,
        sortable: false,
        pinnable: false,
        valueOptions: decomActions.map((action) => {
          const option = {
            label: action.label,
            value: action.value,
          };
          return option;
        }),
        valueGetter: (value: string, row: DecomLogModel) => {
          const actionType = decomActions?.find(
            (action) =>
              action.value === row?.actionModelType ||
              action.label === row?.actionType ||
              action.value === value ||
              action.label === value
          );
          if (actionType) {
            return actionType.label;
          } else {
            return 'No Actions Available';
          }
        },
      },
      {
        field: 'retries',
        headerName: 'Remaining Retries',
        display: 'flex',
        flex: 1,
        maxWidth: 160,
        filterable: false,
        sortable: false,
        pinnable: false,
        valueGetter: (value: number) => Math.max(0, value),
      },
      {
        field: 'acknowledged',
        headerName: 'Acknowledged',
        width: 140,
        type: 'boolean',
        filterable: true,
        sortable: false,
        pinnable: false,
      },
      {
        field: 'actionComplete',
        headerName: 'Action Complete',
        width: 140,
        type: 'boolean',
        filterable: false,
        sortable: false,
        pinnable: false,
      },
      {
        field: 'userId',
        headerName: 'User ID',
        display: 'flex',
        flex: 1,
        width: 140,
        filterable: false,
        sortable: false,
        pinnable: false,
      },
    ],
    []
  );

  const { initialState, computedColumns } = useGridColumns(
    columns,
    'decomLogsGridStates'
  );

  const defaultGridState: GridInitialState = {
    pagination: {
      paginationModel: {
        pageSize: 100,
        page: 0,
      },
      rowCount: 0,
    },
    columns: {
      columnVisibilityModel: {
        userId: false,
        acknowledged: false,
        retries: false,
      },
    },
    filter: {
      filterModel: {
        items: [
          {
            id: 'userEmail',
            field: 'userEmail',
            value: userId,
            operator: 'equals',
          },
          {
            id: 'acknowledged',
            field: 'acknowledged',
            value: 'false',
            operator: 'is',
          },
        ],
      },
    },
  };

  const [paginationModel, setPaginationModel] = useState<{
    pageSize: number;
    page: number;
    cursor: string | null;
    suspendedUserModelId: string | null;
    acknowledged: boolean | null;
  }>({
    pageSize: initialState?.pagination?.paginationModel?.pageSize || 100,
    page: 0,
    cursor: null,
    suspendedUserModelId: userId,
    acknowledged: acknowledgedFilter,
  });
  const [openAcknowledgeDialog, setOpenAcknowledgeDialog] = useState(false);
  const [openCompleteDialog, setOpenCompleteDialog] = useState(false);

  const {
    data: decomLogs,
    isLoading: isLoadingDecomLogs,
    isFetching: isFetchingDecomLogs,
  } = useQuery({
    queryKey: [
      UserKeys.DecomLogs,
      paginationModel.page,
      paginationModel.pageSize,
      paginationModel.cursor,
      paginationModel.suspendedUserModelId,
      paginationModel.acknowledged,
    ],
    queryFn: () => {
      const object: Page<DecomLogModel[]> = {
        results: [],
        firstPull: paginationModel.page === 0,
        searchTime: 0,
        pageSize: paginationModel.pageSize,
        cursor: paginationModel.cursor,
        nameSpace: '',
      };
      return getDecomLogs(
        object,
        paginationModel.suspendedUserModelId,
        paginationModel.acknowledged
      );
    },
    placeholderData: keepPreviousData,
    meta: {
      errorMessage:
        'There was an issue loading the decom logs. Please refresh and try again.',
    },
  });

  const onFilterChange = useCallback(
    (filterModel: GridFilterModel) => {
      const object = { ...paginationModel };

      const hasUserFilter = filterModel.items.find(
        (filter) => filter.field === 'userEmail'
      )?.value;
      const hasAcknowledgedFilter =
        filterModel.items.find((filter) => filter.field === 'acknowledged')
          ?.value !== undefined;

      if (hasUserFilter) {
        const userFilter = filterModel.items.find(
          (filter) => filter.field === 'userEmail'
        )!.value as string;
        object.suspendedUserModelId = userFilter;
      } else if (
        object.suspendedUserModelId !== null &&
        object.suspendedUserModelId !== ''
      ) {
        searchParams.delete('userId');
        setSearchParams(searchParams);
        object.suspendedUserModelId = null;
      }
      if (hasAcknowledgedFilter) {
        const acknowledgedFilter = filterModel.items.find(
          (filter) => filter.field === 'acknowledged'
        )!.value as boolean;
        if (!acknowledgedFilter) {
          setAcknowledgedFilter(null);
        } else {
          setAcknowledgedFilter(acknowledgedFilter);
        }
      } else {
        setAcknowledgedFilter(null);
      }

      object.cursor = null;
      object.page = 0;

      if (JSON.stringify(object) !== JSON.stringify(paginationModel)) {
        setPaginationModel(object);
      }
    },
    [paginationModel, searchParams, setSearchParams]
  );

  const toolbarProps = useMemo(
    () => ({
      selectedRows,
      setPaginationModel: setPaginationModel,
      setOpenAcknowledgeDialog,
      setOpenCompleteDialog,
      acknowledgedFilter,
    }),
    [acknowledgedFilter, selectedRows]
  );

  return (
    <>
      <SharedDataGridServer
        gridKey={viewType.decomLogsGridStates?.activeGridState}
        slots={{
          toolbar: useToolbar(DecomLogGridToolbar, toolbarProps),
          ...createGridOverlays('Decom Logs', <History />),
        }}
        filterOperator={[GridLogicOperator.And]}
        filterPanelSx={{
          minWidth: 840,
        }}
        checkboxSelection
        getRowId={(row) => row.date.toString() + row.userId}
        data={decomLogs}
        columns={computedColumns}
        loading={isLoadingDecomLogs || isFetchingDecomLogs}
        density="compact"
        paginationModel={{
          page: paginationModel.page,
          pageSize: paginationModel.pageSize,
        }}
        onPaginationModelChange={(newModel) => {
          if (isLoadingDecomLogs || isFetchingDecomLogs) return;

          if (newModel.pageSize !== paginationModel.pageSize) {
            setPaginationModel((prev) => ({
              ...prev,
              page: 0,
              pageSize: newModel.pageSize,
              cursor: null,
            }));
          } else {
            setPaginationModel((prev) => ({
              ...prev,
              page: newModel.page,
              pageSize: newModel.pageSize,
              cursor: decomLogs?.cursor || '',
            }));
          }
        }}
        initialState={initialState || defaultGridState}
        getRowHeight={() => 'auto'}
        onFilterModelChange={onFilterChange}
        setSelectedRows={setSelectedRows}
        selectedRows={selectedRows}
      />
      {selectedRows.length > 0 && openAcknowledgeDialog ? (
        <DecomAcknowledgeDialog
          open={openAcknowledgeDialog}
          handleClose={() => {
            setOpenAcknowledgeDialog(false);
          }}
          selectedRows={selectedRows}
          setSelectedRows={setSelectedRows}
          models={decomLogs?.results || []}
        />
      ) : null}
      {selectedRows.length > 0 && openCompleteDialog ? (
        <DecomCompleteDialog
          open={openCompleteDialog}
          handleClose={() => {
            setOpenCompleteDialog(false);
          }}
          selectedRows={selectedRows}
          setSelectedRows={setSelectedRows}
          models={decomLogs?.results || []}
        />
      ) : null}
    </>
  );
}
